import {
  ArrowLeft, ArrowRight, ArrowUpRight, Award, BadgeCheck, Banknote, Bell, Bookmark, Box, Calendar, Camera,
  Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Circle, CircleCheck, CircleHelp, CircleX,
  ClipboardList, Clock, Coins, CreditCard, Download, ExternalLink, Eye, Facebook, FileText, Filter, Flame,
  Gift, Globe, Headphones, Heart, Home, Image, Info, Instagram, Key, Layers, Leaf, Lightbulb, Link, List,
  Lock, Mail, MapPin, Medal, Menu, MessageCircle, MessageSquare, Minus, Music, Package, PackageCheck,
  Palette, Pencil, Percent, Phone, PhoneCall, Play, Plus, Printer, Quote, Receipt, RefreshCw, Repeat,
  Search, Send, Settings, Share2, Shield, ShieldCheck, Shirt, ShoppingBag, ShoppingBasket, ShoppingCart,
  Smile, Sparkles, Star, Sun, Tag, ThumbsDown, ThumbsUp, Timer, Trash2, TrendingUp, TriangleAlert, Truck,
  Upload, User, Users, Video, Wallet, X, Youtube, Zap
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// The glyphs an `icon` element may draw, keyed by the kebab-case names the inspector offers. The whole
// lucide record is 1400 glyphs, which is not something a Moroccan phone should download to draw a tick,
// so the engine names its own set the way each theme names one — and falls back to a dot outside it.
//
// This is the engine's set rather than a theme's: importing a theme's map here would put the shared
// renderer behind one implementation's name, and the other five themes would inherit souk's choices.
//
// It was 56, which is where a merchant looking for a bin, a padlock, a shirt or a receipt met the
// fallback dot and no way to know why. Ninety-eight is still a rounding error against 1400 — lucide is
// tree-shaken, so a glyph nobody names costs a shopper nothing — and it covers what a shop actually
// says: delivery, payment, guarantee, sizing, support, and the handful of marks a page is built from.

export const ELEMENT_ICONS: Readonly<Record<string, LucideIcon>> = {
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up-right': ArrowUpRight,
  award: Award,
  banknote: Banknote,
  bell: Bell,
  calendar: Calendar,
  check: Check,
  'check-circle': CircleCheck,
  'circle-check': CircleCheck,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  clock: Clock,
  'credit-card': CreditCard,
  facebook: Facebook,
  flame: Flame,
  gift: Gift,
  globe: Globe,
  headphones: Headphones,
  heart: Heart,
  home: Home,
  info: Info,
  instagram: Instagram,
  leaf: Leaf,
  lock: Lock,
  mail: Mail,
  'map-pin': MapPin,
  menu: Menu,
  'message-circle': MessageCircle,
  minus: Minus,
  package: Package,
  'package-check': PackageCheck,
  percent: Percent,
  phone: Phone,
  'phone-call': PhoneCall,
  play: Play,
  plus: Plus,
  quote: Quote,
  refresh: RefreshCw,
  search: Search,
  send: Send,
  'shield-check': ShieldCheck,
  'shopping-bag': ShoppingBag,
  'shopping-cart': ShoppingCart,
  sparkles: Sparkles,
  star: Star,
  tag: Tag,
  'thumbs-up': ThumbsUp,
  timer: Timer,
  truck: Truck,
  user: User,
  wallet: Wallet,
  x: X,
  zap: Zap,
  'triangle-alert': TriangleAlert,
  'badge-check': BadgeCheck,
  bookmark: Bookmark,
  box: Box,
  camera: Camera,
  'circle-help': CircleHelp,
  'circle-x': CircleX,
  'clipboard-list': ClipboardList,
  coins: Coins,
  download: Download,
  'external-link': ExternalLink,
  eye: Eye,
  'file-text': FileText,
  filter: Filter,
  image: Image,
  key: Key,
  layers: Layers,
  lightbulb: Lightbulb,
  link: Link,
  list: List,
  medal: Medal,
  'message-square': MessageSquare,
  music: Music,
  palette: Palette,
  pencil: Pencil,
  printer: Printer,
  receipt: Receipt,
  repeat: Repeat,
  settings: Settings,
  'share-2': Share2,
  shield: Shield,
  shirt: Shirt,
  'shopping-basket': ShoppingBasket,
  smile: Smile,
  sun: Sun,
  'thumbs-down': ThumbsDown,
  'trash-2': Trash2,
  'trending-up': TrendingUp,
  upload: Upload,
  users: Users,
  video: Video,
  youtube: Youtube,
};

/** What an icon element draws when it names a glyph the engine does not ship, or names none at all. */
export const ELEMENT_FALLBACK_ICON: LucideIcon = Circle;
